import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Shield, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TermsAndConditions() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate()

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const termsData = [
    {
      title: "Eligibility To Apply",
      content: "You must meet the eligibility requirements set for each scholarship or bursary application. All information provided must be accurate, complete, and truthful.",
      icon: <Shield className="w-5 h-5" />
    },
    {
      title: "Application Process",
      content: "You must submit a completed application with all required documents by the specified deadline. Incomplete and late applications will not be considered.",
      icon: <FileText className="w-5 h-5" />
    },
    {
      title: "Award Criteria",
      content: "The Lagos State Scholarships and bursary are awarded based on availability and the discretion of the selection committee. Amounts and number of awards may vary.",
      icon: <AlertCircle className="w-5 h-5" />
    },
    {
      title: "Privacy",
      content: "By applying, you consent to the collection and use of your personal data as described in our Privacy Policy for application processing and communication purposes by the Lagos State Scholarship Board.",
      icon: <Shield className="w-5 h-5" />
    },
    {
      title: "Use of Portal",
      content: "You agree to use the Portal responsibly and not to submit false or fraudulent information. You are responsible for keeping your account secure.",
      icon: <FileText className="w-5 h-5" />
    },
    {
      title: "Disqualification",
      content: "We may disqualify any applicant who provides false information or violates the terms and conditions of Lagos State Scholarship Board.",
      icon: <AlertCircle className="w-5 h-5" />
    },
    {
      title: "Award Acceptance",
      content: "All award recipients are required to comply with any additional requirements associated with the awarded scholarship or Bursary application, including but not limited to the submission of requested documents to the Lagos State Scholarship Board. Awardees must also participate in public acknowledgment initiatives designed to recognize their achievements and highlight the support received, contributing to the enhancement of their personal and professional reputation.",
      icon: <Shield className="w-5 h-5" />
    },
    {
      title: "Limitation of Liability",
      content: "Lagos State Scholarship Board is not responsible for any errors, losses, or damages related to the Portal or the application process.",
      icon: <AlertCircle className="w-5 h-5" />
    },
    {
      title: "Modifications",
      content: "Lagos State Scholarship Board may update or modify these Terms at any time. Changes will be posted on the Portal and apply immediately.",
      icon: <FileText className="w-5 h-5" />
    },
    {
      title: "Governing Law",
      content: "These terms are governed by the laws of Lagos state compliance and policy, and any disputes will be resolved at the Lagos State Judiciary High court.",
      icon: <Shield className="w-5 h-5" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-yellow-700 text-white py-8 px-4" style={{background: 'linear-gradient(to right, #AF8544, #8B6914)'}}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8" />
            <h1 className="text-2xl md:text-3xl font-bold">Terms and Conditions</h1>
          </div>
          <p className="text-amber-100 text-sm md:text-base">
            Lagos State Scholarship and Bursary Application Portal
          </p>
          <p className="text-amber-200 text-xs md:text-sm mt-2">
            Effective Date: April 2025
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 border-l-4" style={{borderLeftColor: '#AF8544'}}>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Agreement to Terms
          </h2>
          <p className="text-gray-600 leading-relaxed">
            By using the Lagos State Scholarship and Bursary Application Portal, you agree to the following terms and conditions. Please read them carefully before proceeding with your application.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-4">
          {termsData.map((term, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              <button
                onClick={() => toggleSection(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 hover:from-amber-50 hover:to-yellow-50 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <div style={{color: '#AF8544'}}>
                    {term.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm md:text-base">
                      {index + 1}. {term.title}
                    </h3>
                  </div>
                </div>
                <div className="text-gray-500">
                  {expandedSection === index ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>
              </button>
              
              {expandedSection === index && (
                <div className="px-6 py-4 bg-white border-t border-gray-100">
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                    {term.content}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="bg-white text-white rounded-lg p-6 mt-8" style={{background: 'linear-gradient(to right, #AF8544, #8B6914)'}}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Contact Information
          </h3>
          <div className="space-y-2">
            <p className="text-amber-100">
              For questions regarding these terms and conditions:
            </p>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <p className="font-medium">Lagos State Scholarship Board</p>
              <p className="text-amber-100">Website: www.lagosscholarship.org</p>
            </div>
          </div>
        </div>

        {/* Acceptance Checkbox */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-8 border-2 border-dashed border-gray-200">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="acceptTerms" className="text-sm md:text-base text-gray-700 leading-relaxed cursor-pointer">
              I have read, understood, and agree to be bound by these Terms and Conditions for the Lagos State Scholarship and Bursary Application Portal.
            </label>
          </div>
          
          <button
            disabled={!acceptedTerms}
            onClick={()=> navigate("/signup")}
            className={`w-full mt-4 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
              acceptedTerms
                ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {acceptedTerms ? 'Proceed to Application' : 'Accept Terms to Continue'}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-xs md:text-sm">
          <p>© 2025 Lagos State Scholarship Board. All rights reserved.</p>
          <p className="mt-1">
            Last updated: April 2025
          </p>
        </div>
      </div>
    </div>
  );
}